import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FaGlobe } from "react-icons/fa";

export default function LanguageSelector({ onChanged }) {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language || "").toLowerCase();

  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 180 });

  // resolve site primary color from CSS vars (Bootstrap first, then generic)
  const [primary, setPrimary] = useState("#1D3EE3");
  useEffect(() => {
    const root = document.documentElement;
    const tryVar = (name) =>
      getComputedStyle(root).getPropertyValue(name)?.trim();
    const bs = tryVar("--bs-primary");
    const gen = tryVar("--primary");
    setPrimary(bs || gen || "#1D3EE3");
  }, []);

  const toggle = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;

    setPos({
      top: Math.round(r.bottom + 8),
      left: isMobile ? 10 : Math.round(r.right - 180),
      width: isMobile ? window.innerWidth - 20 : 180,
    });
    setOpen((v) => !v);
  };

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const setLang = (code) => {
    if (!code || code === current) return;
    i18n.changeLanguage(code);
    try {
      localStorage.setItem("i18nextLng", code);
    } catch {}
    onChanged?.();
    setOpen(false);
  };

  // simple hover handling for inline styles
  const [hover, setHover] = useState(null);
  const items = [
    { code: "sq", label: "SQ — Shqip" },
    { code: "en", label: "EN — English" },
  ];

  return (
    <>
      <button
        ref={btnRef}
        className="btn btn-link p-0 text-white"
        type="button"
        aria-label="Change language"
        onClick={toggle}
        style={{ lineHeight: 0 }}
      >
        <FaGlobe size={20} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
              background: "#fff",
              borderRadius: 8,
              boxShadow:
                "0 8px 16px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)",
              zIndex: 9999,
              overflow: "hidden",
            }}
          >
            {items.map((it, idx) => {
              const active = current === it.code;
              const hovered = hover === idx;
              return (
                <button
                  key={it.code}
                  className={`w-100 text-start${active ? " active" : ""}`}
                  onClick={() => setLang(it.code)}
                  onMouseEnter={() => setHover(idx)}
                  onMouseLeave={() => setHover(null)}
                  style={itemStyle({ active, hovered, primary })}
                >
                  {it.label}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}

function itemStyle({ active, hovered }) {
  const base = {
    display: "block",
    width: "100%",
    padding: "12px 16px",
    border: "none",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "1rem",
    textAlign: "left",
    transition: "background-color 120ms ease, color 120ms ease",
  };

  if (active) {
    return {
      ...base,
      background: "var(--primary)",
      color: "#fff",
    };
  }

  return {
    ...base,
    background: hovered ? "#f5f5f5" : "#fff",
    color: "#000",
  };
}

